/**
 * @file Deplacement des cibles mobiles (Mover : seek/wander/orbit/flee + recul) et IA data-driven (registre AiBehaviorId -> handler).
 */
import type { AiBehaviorId } from '@config/types';
import type { SimContext } from '../Context';
import type { With } from '../ecs/World';
import { clampToArena } from '../math/geometry';
import { MonsterMode } from '../modes/impl/MonsterMode';
import { Registry } from '../util/Registry';
import type { System } from './System';

const PUSH_DAMP_PER_TICK = 0.8;
const WANDER_PERIOD_TICKS = 40;

/** Deplacement des cibles mobiles (seek/wander/orbit/flee), recul amorti, mise a jour de la grille. */
export const MoverSystem: System = {
  id: 'movers',
  update(ctx: SimContext, dt: number): void {
    const leader = ctx.services.team.leader();
    for (const e of ctx.rt.world.query('target', 'target', 'mover')) {
      const m = e.mover;
      const t = e.transform;
      if (e.visual && e.visual.dying > 0) continue;
      let moved = false;
      switch (m.kind) {
        case 'static':
          break;
        case 'seek': {
          const dx = m.anchorX - t.x;
          const dy = m.anchorY - t.y;
          const d = Math.hypot(dx, dy);
          if (d > 1e-6) {
            m.vx = (dx / d) * m.speed;
            m.vy = (dy / d) * m.speed;
          }
          t.x += m.vx * dt;
          t.y += m.vy * dt;
          moved = true;
          break;
        }
        case 'wander': {
          if ((ctx.rt.tick + e.id) % (WANDER_PERIOD_TICKS + (e.id % 3) * 20) === 0) {
            const a = ctx.rng.spawn.range(0, Math.PI * 2);
            m.vx = Math.cos(a) * m.speed;
            m.vy = Math.sin(a) * m.speed;
          }
          const ox = t.x - m.anchorX;
          const oy = t.y - m.anchorY;
          const od = Math.hypot(ox, oy);
          if (m.radius > 0 && od > m.radius && od > 1e-6) {
            m.vx = (-ox / od) * m.speed;
            m.vy = (-oy / od) * m.speed;
          }
          t.x += m.vx * dt;
          t.y += m.vy * dt;
          const a = ctx.modes.rules.arena;
          const p = clampToArena({ x: t.x, y: t.y }, a.shape, a.halfW, a.halfH, (e.collider?.radius ?? 1) + 0.3);
          t.x = p.x;
          t.y = p.y;
          moved = true;
          break;
        }
        case 'orbit': {
          const ox = t.x - m.anchorX;
          const oy = t.y - m.anchorY;
          const r0 = Math.hypot(ox, oy);
          const angle = r0 > 1e-6 ? Math.atan2(oy, ox) : 0;
          const radius = m.radius > 0 ? m.radius : 6;
          const r = r0 + (radius - r0) * Math.min(1, dt * 2);
          const na = angle + (m.speed / radius) * dt;
          const nx = m.anchorX + Math.cos(na) * r;
          const ny = m.anchorY + Math.sin(na) * r;
          m.vx = (nx - t.x) / dt;
          m.vy = (ny - t.y) / dt;
          t.x = nx;
          t.y = ny;
          moved = true;
          break;
        }
        case 'flee': {
          if (leader) {
            const dx = t.x - leader.transform.x;
            const dy = t.y - leader.transform.y;
            const d = Math.hypot(dx, dy);
            if (d > 1e-6) {
              m.vx = (dx / d) * m.speed;
              m.vy = (dy / d) * m.speed;
            }
          }
          t.x += m.vx * dt;
          t.y += m.vy * dt;
          moved = true;
          break;
        }
      }
      if (m.pushX !== 0 || m.pushY !== 0) {
        t.x += m.pushX;
        t.y += m.pushY;
        const damp = Math.pow(PUSH_DAMP_PER_TICK, dt * ctx.config.sim.hz);
        m.pushX *= damp;
        m.pushY *= damp;
        if (Math.abs(m.pushX) < 1e-4) m.pushX = 0;
        if (Math.abs(m.pushY) < 1e-4) m.pushY = 0;
        moved = true;
      }
      if (moved) {
        if (Math.abs(m.vx) + Math.abs(m.vy) > 1e-4) t.rot = Math.atan2(m.vy, m.vx);
        ctx.rt.targetGrid.update(e.id, t.x, t.y);
      }
    }
  },
};

export type AiHandler = (ctx: SimContext, e: With<'target' | 'ai' | 'mover'>, dt: number) => void;

const BOSS_CHARGE_EVERY = 6;
const BOSS_CHARGE_SECONDS = 1.2;
const BOSS_CHARGE_SPEED_MULT = 3;
const KITE_RADIUS = 8;

/** Fonce vers la barriere du mode Monster ; au contact, inflige ses degats (et se detruit sans recompense). */
const rushBarrier = (ctx: SimContext, e: With<'target' | 'ai' | 'mover'>, onContact?: () => void): void => {
  e.mover.anchorX = 0;
  e.mover.anchorY = 0;
  const mode = ctx.modes.current;
  if (!(mode instanceof MonsterMode)) return;
  if (Math.hypot(e.transform.x, e.transform.y) <= mode.barrierRadius + (e.collider?.radius ?? 0)) {
    onContact?.();
    mode.onBarrierContact(ctx, e);
  }
};

/**
 * Handlers d'IA data-driven (AiBehaviorId -> comportement) :
 *  - chargeBarrier : fonce vers (0,0), contact => MonsterMode.onBarrierContact.
 *  - wanderBoss    : deambule autour de (0,0), charge le chef 1,2 s toutes les 6 s (vitesse x3) ; params.baseSpeed / chargeEvery fixes par le mode (phases du Boss).
 *  - bomberBlast   : comme chargeBarrier, avec explosion visuelle au contact.
 *  - kiteNearest   : orbite autour du chef a 8 u (raid).
 */
export const AI_HANDLERS = new Registry<AiBehaviorId, AiHandler>('AiBehavior')
  .register('none', () => {})
  .register('chargeBarrier', (ctx, e) => rushBarrier(ctx, e))
  .register('wanderBoss', (ctx, e, dt) => {
    const ai = e.ai;
    const m = e.mover;
    if (ai.state === 'init') {
      // un mode (Boss) peut avoir fixe baseSpeed / chargeEvery avant le premier tic : on ne les ecrase pas
      ai.params.baseSpeed ??= m.speed;
      ai.state = 'wander';
      ai.timer = 0;
    }
    const base = ai.params.baseSpeed ?? m.speed;
    ai.timer += dt;
    const leader = ctx.services.team.leader();
    if (ai.state === 'wander') {
      m.kind = 'wander';
      m.anchorX = 0;
      m.anchorY = 0;
      m.speed = base;
      if (ai.timer >= (ai.params.chargeEvery ?? BOSS_CHARGE_EVERY) && leader) {
        ai.state = 'charge';
        ai.timer = 0;
      }
    } else if (leader) {
      m.kind = 'seek';
      m.speed = base * BOSS_CHARGE_SPEED_MULT;
      m.anchorX = leader.transform.x;
      m.anchorY = leader.transform.y;
      if (ai.timer >= BOSS_CHARGE_SECONDS) {
        ai.state = 'wander';
        ai.timer = 0;
      }
    } else {
      ai.state = 'wander';
    }
  })
  .register('bomberBlast', (ctx, e) =>
    rushBarrier(ctx, e, () => ctx.bus.emit('Explosion', { x: e.transform.x, y: e.transform.y, radius: e.ai.params.blastRadius ?? 2.5, behavior: 'explosive' })),
  )
  .register('kiteNearest', (ctx, e) => {
    const leader = ctx.services.team.leader();
    if (!leader) return;
    e.mover.kind = 'orbit';
    e.mover.radius = KITE_RADIUS;
    e.mover.anchorX = leader.transform.x;
    e.mover.anchorY = leader.transform.y;
  });

/** Applique AI_HANDLERS.get(ai.behavior) a chaque cible vivante possedant ai + mover (avant MoverSystem). */
export const EnemyAiSystem: System = {
  id: 'enemyAi',
  update(ctx: SimContext, dt: number): void {
    for (const e of ctx.rt.world.query('target', 'target', 'ai', 'mover')) {
      if (e.visual && e.visual.dying > 0) continue;
      AI_HANDLERS.get(e.ai.behavior)(ctx, e, dt);
    }
  },
};
